import useMembership from "@/shared/hooks/useMembership";
import usePathPickSyncKinds from "@/features/pathpick/hooks/usePathPickSyncKinds";
import usePathPickBasketFilter from "@/features/pathpick/features/basket/hooks/usePathPickBasketFilter";
import useGetPathPickProducts from "@/features/pathpick/hooks/query/useGetPathPickProducts";
import {
  getPathPickBasketProductTypeParams,
  getPathPickBasketRequestChannelParams,
  getPathPickBasketSoldOutParams,
  getPathPickBasketTabParams,
} from "@/features/pathpick/features/basket/utils";
import { BASKET_SORT_ORDER_BY } from "@/features/pathpick/features/basket/constants";
import { PATH_PICK_MARKET_PRICE_BASIS_PARAMS } from "@/features/pathpick/constants";
import { toPathPickRangeFilterParams } from "@/features/pathpick/utils/range-filter";
import { PathPickProduct } from "@/features/pathpick/types";
import { PathPickProductRequestParams } from "@/features/pathpick/types/request-params";

// 응답이 없을 때도 items 참조가 바뀌지 않도록 빈 배열을 공유한다(카드 memo 유지).
const EMPTY_ITEMS: PathPickProduct[] = [];

// 담은 상품 목록 조회 — 검색/상태 탭/페이지네이션(쿼리스트링) + 멤버십 분기를 캡슐화한다.
// page/list 등 여러 위치에서 재사용해도 react-query 캐시를 공유하므로 중복 요청이 없다.
const useGetPathPickBasketProducts = () => {
  const { filter, tab, sort, pageNumber, pageSize, marketPriceBasis } =
    usePathPickBasketFilter();
  // 상품 유형 필터는 핫딜 권한이 있는 멤버십에만 노출된다.
  const { showHotDeal } = useMembership();
  // 등록 신청함 탭이 네이버 외 마켓 연동 이력까지 볼지 — 권한 있는 마켓만.
  const syncKinds = usePathPickSyncKinds();

  // 처리 상태(requestStatus__in)만 서버 파라미터가 없어 의도적으로 요청에서 빠진다
  // — 화면 전용 필터다(usePathPickBasketFilter의 TODO(BE) 참고).
  // 등록 채널은 탭 기본값(3종 전체)을 좁히는 값이라 탭 파라미터 뒤에 전개해 덮어쓴다.
  // 페이지를 뺀 조회 조건 — 검색 결과 전체 선택(ids_only)이 같은 조건으로 다시 조회한다.
  const requestParams: PathPickProductRequestParams = {
    isInBasket: true,
    query: filter.query,
    brandId__in: filter.brandId__in,
    pathradeCategory__in: filter.pathradeCategory__in,
    ...getPathPickBasketTabParams(tab, syncKinds),
    ...getPathPickBasketRequestChannelParams(tab, filter.requestChannel__in),
    ...getPathPickBasketProductTypeParams(
      tab,
      filter.productType__in,
      showHotDeal
    ),
    ...getPathPickBasketSoldOutParams(filter.soldOut),
    // 마진·거래수 범위 — 마진율은 아래 marketPriceBasis 기준으로 서버가 계산한다.
    ...toPathPickRangeFilterParams(filter),
    orderBy: BASKET_SORT_ORDER_BY[sort],
    // 마진 정렬의 계산 기준이자 카드에 표시할 두 마켓 기준가의 기준이다.
    marketPriceBasis: PATH_PICK_MARKET_PRICE_BASIS_PARAMS[marketPriceBasis],
  };
  const { pathPickProductList, isPending, isPlaceholderData } =
    useGetPathPickProducts({ ...requestParams, pageNumber, pageSize });

  // 응답 배열을 그대로 노출한다 — 뷰모델로 감싸면 렌더마다 참조가 바뀌어 카드 memo가 무력화된다.
  const items = pathPickProductList?.products ?? EMPTY_ITEMS;
  const totalCount = pathPickProductList?.totalCount ?? 0;

  return { items, totalCount, isPending, isPlaceholderData, requestParams };
};

export default useGetPathPickBasketProducts;
